// Head-to-head: two models, both directions, per mode, plus their matches (every prompt version and setup).

import { labLogo } from './lab-logos';
import type { HeadToHeadResponse, ModelInfo, PairRow } from '../src/api/contract';
import { api, describeError, isNotFound, modelUrl } from './api';
import { matchList } from './cards';
import { emptyState, errorState, getParam, h, link, loadingState, notFoundState, requireEl, show, timeEl } from './dom';
import { MODE_FILTERS, fmtFixed, fmtInt, fmtPct, modeLabel, parseDate } from './format';
import { HELP } from './help';
import { sortableTable, type Column } from './table';

export async function initH2H(): Promise<void> {
  const root = requireEl('h2h-root');
  const form = requireEl<HTMLFormElement>('h2h-form');
  const selA = requireEl<HTMLSelectElement>('h2h-a');
  const selB = requireEl<HTMLSelectElement>('h2h-b');
  const swap = requireEl<HTMLButtonElement>('h2h-swap');

  const a = getParam('a');
  const b = getParam('b');

  swap.addEventListener('click', () => {
    const va = selA.value;
    selA.value = selB.value;
    selB.value = va;
    if (selA.value && selB.value && selA.value !== selB.value) form.requestSubmit();
  });
  form.addEventListener('submit', (ev) => {
    if (!selA.value || !selB.value || selA.value === selB.value) {
      ev.preventDefault();
      show(root, emptyState('Pick two different models', 'Choose one model in each list, then press Compare.'));
    }
  });

  const modelsTask = api
    .models()
    .then((res) => {
      const models = Array.isArray(res.models) ? res.models : [];
      fillModelSelects(selA, selB, models, a, b);
      // The arena starts with no models at all: say so instead of offering two empty lists.
      if (models.length === 0 && (!a || !b)) {
        show(root, emptyState('No models yet', 'The arena adds free OpenRouter models on its own every few hours. Come back once the first ones have played.'));
      }
    })
    .catch(() => {
      for (const sel of [selA, selB]) sel.replaceChildren(h('option', { attrs: { value: '' } }, 'Could not load models'));
    });

  if (!a || !b) {
    show(root, emptyState('Pick two models', 'Choose two models above. Both directions are compared: each model attacks the other.'));
    await modelsTask;
    return;
  }
  if (a === b) {
    show(root, emptyState('Pick two different models', 'A model never plays against itself.'));
    await modelsTask;
    return;
  }

  const load = async (): Promise<void> => {
    show(root, loadingState('Loading head-to-head'));
    try {
      const data = await api.h2h(a, b);
      if (!data || !data.a || !data.b) throw new Error('The server sent an unexpected response.');
      await renderH2H(root, data);
    } catch (err) {
      if (isNotFound(err)) show(root, notFoundState('Model not found', 'At least one of these models is not in the arena.'));
      else show(root, errorState(describeError(err), () => void load()));
    }
  };
  await Promise.all([modelsTask, load()]);
}

function fillModelSelects(selA: HTMLSelectElement, selB: HTMLSelectElement, models: ModelInfo[], a: string | null, b: string | null): void {
  const sorted = models.filter((m) => m && typeof m.id === 'string').sort((x, y) => (x.displayName || x.id).localeCompare(y.displayName || y.id));
  for (const [sel, value] of [
    [selA, a],
    [selB, b],
  ] as const) {
    const options = [h('option', { attrs: { value: '' } }, 'Choose a model…')];
    for (const m of sorted) options.push(h('option', { attrs: { value: m.id } }, m.isActive ? m.displayName || m.id : `${m.displayName || m.id} (inactive)`));
    if (value && !sorted.some((m) => m.id === value)) options.push(h('option', { attrs: { value } }, value));
    sel.replaceChildren(...options);
    sel.value = value ?? '';
    sel.disabled = false;
  }
}

function directionTable(rows: PairRow[], caption: string): HTMLElement {
  const byMode = MODE_FILTERS.map((mode) => rows.find((r) => r && r.mode === mode)).filter((r): r is PairRow => r !== undefined);
  if (byMode.every((r) => !r.matches)) return emptyState('No matches in this direction yet');
  const columns: Column<PairRow>[] = [
    { key: 'mode', label: 'Defender mode', sortable: false, value: (r) => r.mode, render: (r) => modeLabel(r.mode) },
    { key: 'matches', label: 'Matches', numeric: true, sortable: false, value: (r) => r.matches, render: (r) => fmtInt(r.matches) },
    {
      key: 'avgStage',
      label: 'Stages done',
      help: { term: 'Stages done', text: 'Average number of the six stages the attacker got done per match, out of 6. A stage counts only when it is proven: a tool call that does the forbidden thing, or a secret leaving the chat.' },
      numeric: true,
      sortable: false,
      value: (r) => r.avgStage,
      render: (r) => fmtFixed(r.avgStage, 2),
    },
    { key: 'avgVerbal', label: 'Agreed in words', help: HELP.agreedInWords, numeric: true, sortable: false, value: (r) => r.avgVerbalStage, render: (r) => fmtFixed(r.avgVerbalStage, 2) },
    { key: 'breach', label: 'Breach rate', help: HELP.breachRate, numeric: true, sortable: false, value: (r) => r.breachRate, render: (r) => fmtPct(r.breachRate) },
    { key: 'detect', label: 'Reported', help: { term: 'Reported', text: 'Share of matches where the defender reported the attacker to security.' }, numeric: true, sortable: false, value: (r) => r.detectionRate, render: (r) => fmtPct(r.detectionRate) },
    { key: 'refusal', label: 'Refused to play', help: HELP.refusedToPlay, numeric: true, sortable: false, value: (r) => r.refusalRate, render: (r) => fmtPct(r.refusalRate) },
    { key: 'last', label: 'Last match', sortable: false, value: (r) => parseDate(r.lastMatchAt)?.getTime() ?? null, render: (r) => timeEl(r.lastMatchAt) },
  ];
  return sortableTable({ caption, columns, rows: byMode });
}

function summaryLine(data: HeadToHeadResponse): string {
  const nameA = data.a.displayName || data.a.id;
  const nameB = data.b.displayName || data.b.id;
  const allAB = (data.aAttacksB ?? []).find((r) => r.mode === 'all');
  const allBA = (data.bAttacksA ?? []).find((r) => r.mode === 'all');
  const part = (atk: string, def: string, r: PairRow | undefined): string =>
    r && r.matches > 0
      ? `attacking ${def}, ${atk} got ${fmtFixed(r.avgStage, 2)} of 6 stages done on average (${fmtInt(r.matches)} ${r.matches === 1 ? 'match' : 'matches'})`
      : `${atk} hasn't attacked ${def} yet`;
  const s = `${part(nameA, nameB, allAB)}; ${part(nameB, nameA, allBA)}.`;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

async function renderH2H(root: HTMLElement, data: HeadToHeadResponse): Promise<void> {
  const nameA = data.a.displayName || data.a.id;
  const nameB = data.b.displayName || data.b.id;
  document.title = `${nameA} vs ${nameB} · Head-to-head · Avogadro Arena`;
  const matchesBox = h('div');
  show(
    root,
    h(
      'section',
      { class: 'h2h-head' },
      h('h2', { class: 'vs-title' }, labLogo(data.a.maker, 22), link(modelUrl(data.a.id), nameA), h('span', { class: 'vs' }, ' vs '), labLogo(data.b.maker, 22), link(modelUrl(data.b.id), nameB)),
      h('p', { class: 'lede' }, summaryLine(data)),
    ),
    h(
      'section',
      { class: 'panel panel--atk', attrs: { 'aria-labelledby': 'dir-ab' } },
      h('h2', { attrs: { id: 'dir-ab' } }, `${nameA} attacking ${nameB}`),
      directionTable(Array.isArray(data.aAttacksB) ? data.aAttacksB : [], `${nameA} attacking ${nameB}, by mode`),
    ),
    h(
      'section',
      { class: 'panel panel--def', attrs: { 'aria-labelledby': 'dir-ba' } },
      h('h2', { attrs: { id: 'dir-ba' } }, `${nameB} attacking ${nameA}`),
      directionTable(Array.isArray(data.bAttacksA) ? data.bAttacksA : [], `${nameB} attacking ${nameA}, by mode`),
    ),
    h('section', { class: 'panel', attrs: { 'aria-labelledby': 'h2h-matches' } }, h('h2', { attrs: { id: 'h2h-matches' } }, 'Matches'), matchesBox),
  );
  await matchList(matchesBox, {
    initial: Array.isArray(data.matches) ? data.matches : [],
    // /api/h2h sends the newest 50.
    initialPageSize: 50,
    // Both directions, all prompt versions, like data.matches.
    load: (before) => api.matches({ model: data.a.id, opponent: data.b.id, before, limit: 50 }),
    moreLink: data.matches.length >= 50 ? { href: `/matches?model=${encodeURIComponent(data.a.id)}&opponent=${encodeURIComponent(data.b.id)}`, text: 'All their matches →' } : undefined,
    emptyTitle: 'No matches between these two yet',
    emptyDetail: 'The scheduler covers every pairing in turn; check back later.',
  });
}
